/**
 * Shader Node — PostMessage Types & Shared Definitions
 *
 * Discriminated unions for type-safe message handling between
 * the host (React app) and the sandboxed iframe (user code).
 */

import type { ShaderInputPayloads, ShaderInputType } from './media/contract';

// ---------------------------------------------------------------------------
// Runtime Modes
// ---------------------------------------------------------------------------

/** Supported shader runtime modes */
export type RuntimeMode = 'p5' | 'glsl' | 'threejs' | 'raw';

// ---------------------------------------------------------------------------
// Extracted Parameters (from `// @param` comments)
// ---------------------------------------------------------------------------

/** Parameter types extractable from code comments */
export type ParamType = 'float' | 'int' | 'bool' | 'color' | 'select';

/** A single parameter extracted from user code via `// @param` annotations */
export interface ExtractedParam {
  name: string;
  type: ParamType;
  min?: number;
  max?: number;
  default: number | string | boolean;
  options?: string[];  // For 'select' type
  label?: string;
}

// ---------------------------------------------------------------------------
// Host → Iframe Messages
// ---------------------------------------------------------------------------

/**
 * What a `genie:input` message carries: one of the contract payloads (see
 * `media/contract.ts`), or `null` to clear a disconnected input.
 */
export type HostInputData =
  | ShaderInputPayloads[ShaderInputType]
  | Record<string, unknown>
  | null;

/** Send input data to the iframe. `null` clears a disconnected input. */
export interface HostInputMessage {
  type: 'genie:input';
  handle: ShaderInputType | (string & {});
  data: HostInputData;
}

/** Update a single parameter value */
export interface HostParamMessage {
  type: 'genie:param';
  name: string;
  value: number | string | boolean;
}

/** Control the sketch lifecycle */
export interface HostControlMessage {
  type: 'genie:control';
  action: 'pause' | 'resume' | 'restart' | 'capture';
}

/** Discriminated union of all host-to-iframe messages */
export type HostMessage =
  | HostInputMessage
  | HostParamMessage
  | HostControlMessage;

// ---------------------------------------------------------------------------
// Iframe → Host Messages
// ---------------------------------------------------------------------------

/** Output data from user code (frames, data, etc.) */
export interface IframeOutputMessage {
  type: 'genie:output';
  handle: string;
  data: ArrayBuffer | string | Record<string, unknown>;
}

/** Error report from the iframe */
export interface IframeErrorMessage {
  type: 'genie:error';
  kind?: ShaderErrorKind;
  message: string;
  line?: number;
  column?: number;
}

/** Heartbeat / watchdog ping from the iframe */
export interface IframeHeartbeatMessage {
  type: 'genie:heartbeat';
}

/** Ready signal — iframe has finished bootstrapping */
export interface IframeReadyMessage {
  type: 'genie:ready';
}

/**
 * The document finished loading: the runtime library and the user's code have run
 * (the window `load` event). `genie:ready` comes earlier, before either.
 */
export interface IframeLoadedMessage {
  type: 'genie:loaded';
}

/** Captured frame data (response to 'capture' control) */
export interface IframeCaptureMessage {
  type: 'genie:capture';
  dataUrl: string;
}

/** Discriminated union of all iframe-to-host messages */
export type IframeMessage =
  | IframeOutputMessage
  | IframeErrorMessage
  | IframeHeartbeatMessage
  | IframeReadyMessage
  | IframeLoadedMessage
  | IframeCaptureMessage;

// ---------------------------------------------------------------------------
// Shader Error (exposed to consumers)
// ---------------------------------------------------------------------------

/**
 * Where an error came from. `unresponsive` clears itself when heartbeats
 * resume; `input` belongs to a connected input (image, video, …), not to the code.
 */
export type ShaderErrorKind = 'compile' | 'runtime' | 'unresponsive' | 'context-lost' | 'input';

/** Structured error from the shader runtime */
export interface ShaderError {
  kind?: ShaderErrorKind;
  message: string;
  line?: number;
  column?: number;
}

// ---------------------------------------------------------------------------
// CDN Configuration
// ---------------------------------------------------------------------------

/** Default CDN URLs for runtime libraries (loaded inside iframe only) */
export const CDN_DEFAULTS: Record<RuntimeMode, string[]> = {
  p5: ['https://cdn.jsdelivr.net/npm/p5@1.11.3/lib/p5.min.js'],
  glsl: [], // No external CDN needed — uses raw WebGL
  // 0.160.x is the last release that ships the global-`THREE` UMD build the sketches use.
  threejs: ['https://cdn.jsdelivr.net/npm/three@0.160.1/build/three.min.js'],
  raw: [], // No external CDN needed
};

/** Subresource integrity for the default CDN scripts (pinned versions above). */
export const CDN_INTEGRITY: Readonly<Record<string, string>> = {
  'https://cdn.jsdelivr.net/npm/p5@1.11.3/lib/p5.min.js': 'sha384-6FJh+LJ4p/43R8LpkZjM6iDKaXx59crdjiBG6n2sioNPNXc+c2iNpBvO2bQ25nSH',
  'https://cdn.jsdelivr.net/npm/three@0.160.1/build/three.min.js': 'sha384-qOkzR5Ke/XkQxuGVJ9hpFEpDlcoLtWwVYhnJf06cLIZa2vaIptSqaubivErzmD5O',
};
